import {CommentsCategory} from '@domain/comments'
import {useScrollDrag} from '@domain/shared'
import {useMediaQueryBreakpoints} from '@hconnect/common/hooks'
import {moveElementsHorizontally} from '@hconnect/ganttchart/src/helpers/chart.helpers'
import {useGanttChartCells} from '@hconnect/ganttchart/src/hooks/useGanttChartCells'
import {dataTestId} from '@hconnect/uikit'
import {Box, Stack, useTheme} from '@mui/material'
import {useQueryErrorResetBoundary} from '@tanstack/react-query'
import {useDateScale} from '@widgets/common-d3-chart'
import {SelectedAssetCommentProvider, useCommentsPermissions} from '@widgets/planning-comments'
import {
  usePlanningChartStartEnd,
  calculateRangeWithinBoundary
} from '@widgets/planning-schedule-chart'
import React, {useCallback, useLayoutEffect, useRef} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {
  ScheduleCostAvoidanceDialogProvider,
  PlannerFeature,
  usePlannerFeature,
  useElementWidth
} from '../hooks'

import {CostAvoidanceDialog} from './dailyPlanning/costAvoidance'
import {PlanningElectricityChartCard} from './dailyPlanning/electricity-chart'
import {KPIsSection} from './dailyPlanning/kpi-section'
import {ProductionPlanningChartCard} from './dailyPlanning/scheduleCard'
import {StockLevelsCard} from './dailyPlanning/stockLevels'
import {PlannerErrorBoundary, ProductionPlanningSlider} from './shared'

interface ProductionPlanningChartContainerProps {
  plantCode: string
}

const _ProductionPlanningChartContainer: React.FC<ProductionPlanningChartContainerProps> = ({
  plantCode
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {spacing} = useTheme()
  const {sm: isMobile} = useMediaQueryBreakpoints()
  const {hoursRange, setHoursRange, hoursList, stepsPerHour, totalSteps, startOfPlan, endOfPlan} =
    usePlanningChartStartEnd()
  const areCommentsEnabled = usePlannerFeature(PlannerFeature.PlanningComments)
  const {canViewComments: canViewScheduleItemComments} = useCommentsPermissions(
    CommentsCategory.AssetOperationTimes
  )
  const {canViewComments: canViewAssetComments} = useCommentsPermissions(CommentsCategory.Assets)
  const shouldShowScheduleItemComments = areCommentsEnabled && canViewScheduleItemComments
  const shouldShowAssetComments = areCommentsEnabled && canViewAssetComments

  const [planningLayoutWrapperRef, planningLayoutWidth] = useElementWidth()
  const cardXPadding = parseInt(spacing(isMobile ? 2 : 3))
  // calculating available width for gantt and electricity chart
  const availableWidth = planningLayoutWidth > 0 ? planningLayoutWidth - 2 * cardXPadding : 0
  // constants for layout of the Gantt chart
  const labelColumnWidth = isMobile ? 100 : 120
  const dateLabelHeight = 24
  const hourlyPriceHeight = 30
  const cellHeight = 50
  const chartWidth = availableWidth === 0 ? 0 : availableWidth - labelColumnWidth
  const {updateGanttCells, cellWidth, cellsDisplayed} = useGanttChartCells({
    hoursDisplayed: hoursRange[1] - hoursRange[0],
    cellsPerHour: stepsPerHour,
    chartWidth
  })

  const ganttChartRef = useRef<HTMLDivElement>(null)
  const dragScrollWrapperRef = useRef<HTMLDivElement>(null)

  const [immediateHourOffset] = hoursRange

  // Horizontal time scale
  const xScale = useDateScale({
    domain: [startOfPlan, endOfPlan],
    range: [0, cellWidth * totalSteps]
  })

  const updateHourRangeOnScrollOrDrag = useCallback(
    (delta: number) => {
      const currentOffset = immediateHourOffset + delta
      const newRange = calculateRangeWithinBoundary(
        [currentOffset, currentOffset + cellsDisplayed],
        [0, totalSteps]
      )
      setHoursRange(newRange)
    },
    [immediateHourOffset, cellsDisplayed, setHoursRange, totalSteps]
  )
  // we need this effect to move the charts horizontally when the slider is moved
  useLayoutEffect(() => {
    moveElementsHorizontally(cellWidth * immediateHourOffset, [ganttChartRef])
  }, [cellWidth, immediateHourOffset])

  useScrollDrag({
    ref: dragScrollWrapperRef,
    callback: updateHourRangeOnScrollOrDrag,
    stepWidth: cellWidth,
    multiplier: 4,
    isDragEnabled: false
  })

  const {reset} = useQueryErrorResetBoundary()

  return (
    <Box {...dataTestId('planning_chart_container')} ref={planningLayoutWrapperRef}>
      <Stack spacing={2}>
        <ProductionPlanningSlider updateGanttCells={updateGanttCells} />
        <PlannerErrorBoundary
          contextName="Stock levels card"
          onRetry={reset}
          title={t('error.storageLevelCardBoundaryError')}
          fallbackTestId="stock_levels_card_fallback"
        >
          <StockLevelsCard plantCode={plantCode} />
        </PlannerErrorBoundary>
        <Stack spacing={2} ref={dragScrollWrapperRef} {...dataTestId('chart_scroll_drag_wrapper')}>
          <ScheduleCostAvoidanceDialogProvider>
            <PlannerErrorBoundary
              contextName="Production planning card"
              onRetry={reset}
              title={t('error.productionPlanningCardBoundaryError')}
              fallbackTestId="production_planning_card_fallback"
            >
              <SelectedAssetCommentProvider>
                <CostAvoidanceDialog
                  plantCode={plantCode}
                  startOfPlan={startOfPlan}
                  endOfPlan={endOfPlan}
                />
                <ProductionPlanningChartCard
                  plantCode={plantCode}
                  ref={ganttChartRef}
                  startOfPlan={startOfPlan}
                  endOfPlan={endOfPlan}
                  labelColumnWidth={labelColumnWidth}
                  dateLabelHeight={dateLabelHeight}
                  hourlyPriceHeight={hourlyPriceHeight}
                  cellHeight={cellHeight}
                  cellWidth={cellWidth}
                  hoursDisplayed={cellsDisplayed}
                  xScale={xScale}
                  hoursList={hoursList}
                  shouldShowScheduleItemComments={shouldShowScheduleItemComments}
                  shouldShowAssetComments={shouldShowAssetComments}
                />
              </SelectedAssetCommentProvider>
            </PlannerErrorBoundary>
            <PlannerErrorBoundary
              contextName="Electricity chart card"
              onRetry={reset}
              title={t('error.electricityChartCardBoundaryError')}
              fallbackTestId="electricity_chart_card_fallback"
            >
              <PlanningElectricityChartCard
                startOfPlan={startOfPlan}
                endOfPlan={endOfPlan}
                hoursList={hoursList}
                cellWidth={cellWidth}
                labelColumnWidth={labelColumnWidth}
                cellsDisplayed={cellsDisplayed}
              />
            </PlannerErrorBoundary>
          </ScheduleCostAvoidanceDialogProvider>
        </Stack>
        <PlannerErrorBoundary
          contextName="KPIs section"
          onRetry={reset}
          title={t('error.kpiCardBoundaryError')}
          fallbackTestId="kpi_card_fallback"
        >
          <KPIsSection plantCode={plantCode} />
        </PlannerErrorBoundary>
      </Stack>
    </Box>
  )
}

export const ProductionPlanningChartContainer = React.memo(_ProductionPlanningChartContainer)
